import React, { useEffect, useRef, useState } from "react";
import { Biohazard, Droplet } from "lucide-react";

const TARGET = 12;
const CELLS = 16;

export const PurgeGame = ({ onWin, onPenalty }) => {
  const cellsRef = useRef(Array(CELLS).fill(null));
  const timers = useRef(new Set());
  const idRef = useRef(0);
  const hitsRef = useRef(0);
  const doneRef = useRef(false);
  const [, setTick] = useState(0);
  const [hits, setHits] = useState(0);

  const later = (fn, ms) => {
    const t = setTimeout(() => {
      timers.current.delete(t);
      fn();
    }, ms);
    timers.current.add(t);
  };

  const setCell = (i, val) => {
    const next = [...cellsRef.current];
    next[i] = val;
    cellsRef.current = next;
    setTick((t) => t + 1);
  };

  useEffect(() => {
    const allTimers = timers.current;
    const spawn = setInterval(() => {
      if (doneRef.current) return;
      const empty = cellsRef.current.map((c, i) => (c ? -1 : i)).filter((i) => i >= 0);
      if (!empty.length) return;
      const i = empty[Math.floor(Math.random() * empty.length)];
      idRef.current += 1;
      const id = idRef.current;
      const type = Math.random() < 0.22 ? "clean" : "spore";
      setCell(i, { id, type });
      later(() => {
        if (cellsRef.current[i]?.id === id) setCell(i, null);
      }, type === "clean" ? 1500 : 1150);
    }, 520);
    return () => {
      clearInterval(spawn);
      allTimers.forEach(clearTimeout);
    };
  }, []);

  const hit = (i) => {
    const cell = cellsRef.current[i];
    if (!cell || cell.hit || doneRef.current) return;
    if (cell.type === "clean") {
      setCell(i, { ...cell, hit: true, bad: true });
      onPenalty(3);
    } else {
      setCell(i, { ...cell, hit: true });
      hitsRef.current += 1;
      setHits(hitsRef.current);
      if (hitsRef.current >= TARGET) {
        doneRef.current = true;
        onWin();
      }
    }
    later(() => {
      if (cellsRef.current[i]?.id === cell.id) setCell(i, null);
    }, 260);
  };

  return (
    <div className="space-y-5" data-testid="purge-game">
      <div className="flex items-center justify-between font-tac text-sm">
        <span className="text-slate-400">SPORES BURNED</span>
        <span data-testid="purge-hit-count" className="text-emerald-300 tabular-nums text-lg">{hits} / {TARGET}</span>
      </div>
      <div className="grid grid-cols-4 gap-2 sm:gap-3 max-w-md mx-auto">
        {cellsRef.current.map((cell, i) => (
          <button
            key={i}
            type="button"
            data-testid={`purge-cell-${i}`}
            data-state={cell ? cell.type : "empty"}
            onPointerDown={() => hit(i)}
            className="aspect-square rounded-sm border border-slate-800 bg-slate-950 spore-grid-bg flex items-center justify-center relative overflow-hidden"
          >
            {cell && !cell.hit && cell.type === "spore" && <Biohazard className="h-2/3 w-2/3 text-[#39FF14] spore-pop drop-shadow-[0_0_10px_rgba(57,255,20,0.7)]" />}
            {cell && !cell.hit && cell.type === "clean" && <Droplet className="h-1/2 w-1/2 text-sky-400 spore-pop" />}
            {cell?.hit && <span className={`absolute inset-0 spore-burst ${cell.bad ? "bg-sky-500/40" : "bg-[#FF2E63]/50"}`} />}
          </button>
        ))}
      </div>
    </div>
  );
};
